import { Popover, PopoverContent, PopoverTrigger } from "@heroui/react"

import type { JsonFieldValue } from "@/types/json"

import { TruncatedCell } from "./TruncatedCell"

export const ExpectedOutputJsonCell = ({
  value,
}: {
  value: JsonFieldValue | undefined
}) => {
  if (
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  ) {
    return <span className="text-coval-text-muted">-</span>
  }

  if (typeof value !== "object" || Array.isArray(value)) {
    const displayValue =
      typeof value === "string" ? value : JSON.stringify(value, null, 2)

    return (
      <Popover placement="bottom-start">
        <PopoverTrigger>
          <div className="cursor-pointer hover:bg-surface-1 rounded p-1 max-w-[200px]">
            <TruncatedCell value={displayValue} maxLines={2} />
          </div>
        </PopoverTrigger>
        <PopoverContent className="max-w-[400px] max-h-[300px] overflow-auto p-3">
          <pre className="text-sm text-coval-text whitespace-pre-wrap break-all">
            {displayValue}
          </pre>
        </PopoverContent>
      </Popover>
    )
  }

  const entries = Object.entries(value)
  const displayLimit = 3
  const hasMore = entries.length > displayLimit

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <div className="cursor-pointer hover:bg-surface-1 rounded p-1 max-w-[200px]">
          <div className="space-y-0.5">
            {entries.slice(0, displayLimit).map(([key, val]) => (
              <div key={key} className="flex items-center gap-1 text-sm">
                <span className="text-coval-text-muted font-medium truncate max-w-[60px]">
                  {key}:
                </span>
                <span className="truncate text-coval-text">
                  {typeof val === "object" ? JSON.stringify(val) : String(val)}
                </span>
              </div>
            ))}
            {hasMore && (
              <span className="text-xs text-coval-text-muted">
                +{entries.length - displayLimit} more
              </span>
            )}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="max-w-[400px] max-h-[300px] overflow-auto p-3">
        <div className="space-y-1">
          {entries.map(([key, val]) => (
            <div key={key} className="flex gap-2 text-sm">
              <span className="text-coval-text-muted font-medium shrink-0">
                {key}:
              </span>
              <span className="text-coval-text break-all">
                {typeof val === "object"
                  ? JSON.stringify(val, null, 2)
                  : String(val)}
              </span>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
